import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRightFromBracket, faChevronDown, faChevronUp, faCircleMinus, faUserGear, faUserMinus, faUserPlus } from '@fortawesome/pro-solid-svg-icons'
import { useAuthContext, useLogout } from '../../context/auth.context'
import { OffcanvasClose } from '../../shared/component/offcanvas'
import { AccountAvatar, AccountHint } from '../../shared/recent-accounts/account-view'
import { emailKey, forgetAccount, forgetAllAccounts, readRecentAccounts } from '../../shared/recent-accounts/recent-accounts'
import { displayNameOf, isImpersonated } from '../../shared/recent-accounts/sign-in-memory'
import { ADD_ACCOUNT_PATH, noteSignInHandoff, openSignInTab, signInTabPath } from '../../shared/recent-accounts/sign-in-handoff'
import '../../shared/sso-workspace/sso-workspace.scss'
import './account-menu-accounts.scss'

// The account part of the account menu (owner 2026-11-02, "like Google's"),
// at the top of the panel, and everything about the account is here (the
// panel's old Logout and "Forget this account on this device" items are
// gone):
//
//   - the account card: the account this tab is signed in to, a rounded
//     block like the ones it opens to, floating in the panel (owner
//     2026-11-03: "the header being rounded like the items it expands ...
//     just floating when closed") -- initials, name, email, a "Signed in"
//     pill ("Impersonating" in an impersonated tab) and a round chevron
//     button (aria-expanded). It never changes but for the chevron.
//   - Sign out, one row under it, always there (owner: exposed while
//     closed too); never twice.
//   - the chevron opens a recessed tray behind the card: its sunk ground
//     fades in around the card, which lifts, while the well slides open
//     under the card inside the same tray ("it is opening up behind it
//     with the items below it"); closing runs both back (0.2 s; at once
//     for anyone who asks for less motion). In the well, each in its own
//     rounded block:
//       every other account this browser remembers (the sign-in page's
//       recent accounts: the same list and rules; none in an impersonated
//       tab): the row itself is Sign in, which opens that account's sign-in
//       in a NEW tab that starts signed out (no opener, so no copy of this
//       tab's session) while this tab stays signed in (sign-in-handoff.js).
//       No status pill: whether it is signed in in another tab can't be
//       known here;
//       "Add another account": the sign-in page's email field in a new tab;
//       "Remove an account": edit mode, below;
//       "Manage your EducationXR account": the account's settings page.
//
// Edit mode mirrors the sign-in page's chooser (account-chooser.js), so
// someone who learned one knows the other: "Remove an account" with the
// same line of help, a red minus-circle Remove on every remembered account
// -- this tab's own too, which forgets it on this device only: the tab stays
// signed in -- then Done and the quiet "Forget all accounts on this device".
// Entering it focuses the first Remove; a removal is said in the status line
// and moves focus to the next Remove (or the one before, or Done); Done
// returns to the "Remove an account" row. Nothing to remove left ends it.
// Never in an impersonated tab: nothing of an impersonation is remembered,
// or forgotten, here.
//
// No line wraps (owner 2026-11-02): name, email and hint each keep to one
// line. An email too long for the panel is shortened in the middle -- the
// local part gives way, the domain stays whole -- and its whole address is
// named, and shown on a tap (or, inside a row that is itself a button, when
// the row has focus).
// The list is read again each time the menu opens: a sign-in records after
// the header mounts, and another tab can change it.
export const ACCOUNT_MENU_MORE = 'account-menu-more'
export const MANAGE_ACCOUNT_PATH = '/org/login-setting'
// The closing slide's length (account-menu-accounts.scss), and a little
// more: the well is hidden then even if its animationend never comes.
const WELL_CLOSE_MS = 200
const WELL_CLOSE_FALLBACK_MS = WELL_CLOSE_MS + 100

// The chooser's words (account-chooser.js, login-email.js).
export const REMOVE_HELP = "Removing an account only forgets it on this device. It doesn't sign anyone out or delete anything."
export const THIS_ACCOUNT_FORGOTTEN = "This account won't be suggested when you sign in on this device."
export const ALL_FORGOTTEN = 'No accounts are remembered on this device now.'

// No slide where motion is turned down, or where it can't be asked (jsdom).
const reducedMotion = () => {
  try {
    return !window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch (e) {
    return true
  }
}

// The width of some text in an element's font, or null where the browser
// can't measure (the tests' jsdom never gets here: its boxes are 0 wide).
let measureCanvas = null
const textWidth = (text, font) => {
  try {
    measureCanvas = measureCanvas || document.createElement('canvas')
    const context = measureCanvas.getContext('2d')
    if (!context) return null
    context.font = font
    return context.measureText(text).width
  } catch (e) {
    return null
  }
}

// An address on one line. Too long for it, it is shortened in the middle:
// as much of the local part as fits, "…", then the whole domain, measured
// to the character (a CSS ellipsis left a gap before the "@"). Shortened,
// it is a button named with the whole address that shows all of it (and
// shortens it again) on a tap or Enter -- never only on hover. Inside a row
// that is itself a button (interactive false) it can't be one: the row
// names the whole address and shows it while it has focus (expand).
const EmailLine = ({ email, className = '', interactive = true, expand = false }) => {
  const at = (email || '').lastIndexOf('@')
  const local = at > 0 ? email.slice(0, at) : email || ''
  const domain = at > 0 ? email.slice(at) : ''

  const [node, updateNode] = useState(null)
  // how many characters of the local part are shown; null: all of them
  const [keep, updateKeep] = useState(null)
  const [tapped, updateTapped] = useState(false)
  const whole = interactive ? tapped : expand

  const measure = useCallback(
    () => {
      if (!node || whole) return
      const width = node.clientWidth - 1
      if (width <= 0) return
      const style = window.getComputedStyle(node)
      const font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`
      const full = textWidth(`${local}${domain}`, font)
      if (full === null || full <= width) {
        updateKeep(null)
        return
      }
      const room = width - textWidth(domain, font)
      let low = 1
      let high = local.length - 1
      let best = 1
      while (low <= high) {
        const mid = Math.floor((low + high) / 2)
        if (textWidth(`${local.slice(0, mid)}…`, font) <= room) {
          best = mid
          low = mid + 1
        } else {
          high = mid - 1
        }
      }
      updateKeep(best)
    },
    [node, whole, local, domain]
  )

  useEffect(
    () => {
      measure()
      if (!node || typeof window.ResizeObserver !== 'function') return undefined
      const observer = new window.ResizeObserver(() => measure())
      observer.observe(node)
      return () => observer.disconnect()
    },
    [node, measure]
  )

  const short = keep !== null && !whole
  const parts =
    <React.Fragment>
      <span className="am-mail-local">{short ? `${local.slice(0, keep)}…` : local}</span>
      {domain && <span className="am-mail-domain">{domain}</span>}
    </React.Fragment>

  if ((keep !== null || whole) && interactive) {
    return (
      <button
        ref={updateNode}
        type="button"
        className={`am-mail am-mail-button ${whole ? 'am-mail-whole' : ''} ${className}`}
        aria-label={email}
        aria-expanded={whole}
        title={email}
        onClick={() => updateTapped(!tapped)}>
        {parts}
      </button>
    )
  }

  if (keep !== null || whole) {
    return <span ref={updateNode} className={`am-mail ${whole ? 'am-mail-whole' : ''} ${className}`}>{parts}</span>
  }

  // It fits: one text node, read as one address.
  return <span ref={updateNode} className={`am-mail am-mail-fits ${className}`}>{email}</span>
}

// Line 1 the name, line 2 the email; with no name, the email is line 1.
const AccountLines = ({ account, interactive, expand }) =>
  account.name
    ? <React.Fragment>
        <span className="am-name" title={account.name}>{account.name}</span>
        <EmailLine email={account.email} interactive={interactive} expand={expand} />
      </React.Fragment>
    : <EmailLine email={account.email} className="am-name" interactive={interactive} expand={expand} />

const accountLabel = account => (account.name ? `${account.name} (${account.email})` : account.email)

// Another account, not editing: the whole row is Sign in (a new tab).
const SignInRow = ({ account, target, onSignIn }) => {
  const [focused, updateFocused] = useState(false)
  return (
    <li className="am-other-item" data-testid="account-menu-other">
      <OffcanvasClose target={target}>
        <button
          type="button"
          className="am-block am-other am-other-button"
          aria-label={`Sign in to ${accountLabel(account)} in a new tab`}
          onFocus={() => updateFocused(true)}
          onBlur={() => updateFocused(false)}
          onClick={() => onSignIn(account)}>
          <AccountAvatar account={account} />
          <span className="am-text">
            <AccountLines account={account} interactive={false} expand={focused} />
            <AccountHint account={account} />
          </span>
        </button>
      </OffcanvasClose>
    </li>
  )
}

// Edit mode: any remembered account, with the chooser's red minus-circle.
const RemoveRow = ({ account, isCurrent, removeRef, onRemove }) =>
  <li className="am-block am-other am-edit" data-testid="account-menu-edit-row">
    <AccountAvatar account={account} />
    <span className="am-text">
      <AccountLines account={account} />
      {isCurrent
        ? <span className="am-edit-current"><span className="sw-pill ok">Signed in</span></span>
        : <AccountHint account={account} />}
    </span>
    <button
      type="button"
      ref={removeRef}
      className="sa-account-remove"
      aria-label={`Remove ${accountLabel(account)} from this device`}
      onClick={onRemove}>
      <FontAwesomeIcon icon={faCircleMinus} />
    </button>
  </li>

export const AccountMenuAccounts = ({ target }) => {
  const authContext = useAuthContext()
  const claims = authContext.state.jwtClaims || {}
  const email = claims.email || ''
  const impersonating = isImpersonated(claims)
  const logout = useLogout()

  const [accounts, updateAccounts] = useState(() => readRecentAccounts())
  const [open, updateOpen] = useState(false)
  const [editing, updateEditing] = useState(false)
  // Closing: the well stays drawn (inert) while it slides closed, then is
  // hidden on its animationend, or by the fallback timer.
  const [closing, updateClosing] = useState(false)
  const closeTimer = useRef(null)
  // { text, n, focus }: n moves on every change, so focus lands (and the
  // line is read) again even when the words repeat. focus: an account's key
  // (its Remove), 'done', 'status' or 'edit' (the "Remove an account" row).
  const [said, updateSaid] = useState({ text: '', n: 0, focus: null })
  const removeRefs = useRef({})
  const doneRef = useRef(null)
  const editRowRef = useRef(null)
  const statusRef = useRef(null)

  const finishClosing = useCallback(() => {
    clearTimeout(closeTimer.current)
    closeTimer.current = null
    updateClosing(false)
  }, [])

  useEffect(() => () => clearTimeout(closeTimer.current), [])

  const handleToggle = () => {
    if (!open) {
      finishClosing() // opening again mid-slide: it slides open from there
      updateOpen(true)
      return
    }
    updateOpen(false)
    updateEditing(false)
    if (reducedMotion()) return
    updateClosing(true)
    clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(finishClosing, WELL_CLOSE_FALLBACK_MS)
  }

  useEffect(
    () => {
      const panel = target && document.getElementById(target)
      if (!panel) return undefined
      const reset = () => {
        updateAccounts(readRecentAccounts())
        updateOpen(false)
        updateEditing(false)
        finishClosing()
        updateSaid({ text: '', n: 0, focus: null })
      }
      panel.addEventListener('show.bs.offcanvas', reset)
      return () => panel.removeEventListener('show.bs.offcanvas', reset)
    },
    [target, finishClosing]
  )

  useEffect(
    () => {
      if (!said.n || !said.focus) return
      const element = {
        done: doneRef.current,
        status: statusRef.current,
        edit: editRowRef.current
      }[said.focus] || removeRefs.current[said.focus]
      if (element) element.focus()
      else if (statusRef.current) statusRef.current.focus()
    },
    [said]
  )

  const currentKey = emailKey(email)
  const others = impersonating ? [] : accounts.filter(account => emailKey(account.email) !== currentKey)
  // Edit mode removes any remembered account, this tab's own too.
  const removable = impersonating ? [] : accounts
  const canEdit = removable.length > 0

  // The current account as the token names it; its picture, if this browser
  // ever learned one, from its remembered entry.
  const remembered = accounts.find(account => emailKey(account.email) === currentKey)
  const current = { email, name: displayNameOf(claims), avatarURL: remembered && remembered.avatarURL }

  const handleSignIn = account => openSignInTab(signInTabPath(account.email, noteSignInHandoff(account.email)))

  const say = (text, focus) => updateSaid(prev => ({ text, n: prev.n + 1, focus }))

  const handleEdit = () => {
    if (!canEdit) return
    updateEditing(true)
    // the first Remove, as the chooser's remove mode
    say('', emailKey(removable[0].email))
  }

  const handleDone = () => {
    updateEditing(false)
    say('', 'edit')
  }

  const handleRemove = (account, index) => {
    const remaining = forgetAccount(account.email)
    updateAccounts(remaining)
    const text = emailKey(account.email) === currentKey ? THIS_ACCOUNT_FORGOTTEN : `${account.email} won't be suggested when you sign in on this device.`
    if (!remaining.length) {
      // nothing left to remove: edit mode ends, and the line takes focus
      updateEditing(false)
      say(text, 'status')
      return
    }
    const next = remaining[Math.min(index, remaining.length - 1)]
    say(text, emailKey(next.email))
  }

  const handleForgetAll = () => {
    updateAccounts(forgetAllAccounts())
    updateEditing(false)
    say(ALL_FORGOTTEN, 'status')
  }

  return (
    <section className="am-accounts" aria-label="Accounts" data-testid="account-menu-accounts">
      {/* The tray: transparent while closed (the card floats on the panel);
          open, its sunk ground fades in behind the card and the well. */}
      <div className={`am-tray${open ? ' am-tray-open' : ''}`} data-testid="account-menu-tray">
        <div className="am-block am-current" data-testid="account-menu-current">
          <AccountAvatar account={current} />
          <div className="am-text">
            <span className="visually-hidden">Signed in as </span>
            <AccountLines account={current} />
            <div className="am-current-foot">
              <span className={`sw-pill ${impersonating ? 'warn' : 'ok'}`}>{impersonating ? 'Impersonating' : 'Signed in'}</span>
              <button
                type="button"
                className="am-toggle"
                aria-label="Your accounts"
                title="Your accounts"
                aria-expanded={open}
                aria-controls={ACCOUNT_MENU_MORE}
                onClick={handleToggle}
                data-testid="account-menu-toggle">
                <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
              </button>
            </div>
          </div>
        </div>

        {/* The well, under the card inside the tray: hidden outright when
            closed. The inner box is what the slide clips. */}
        <div
          id={ACCOUNT_MENU_MORE}
          className={`am-well${closing ? ' am-well-closing' : ''}`}
          hidden={!open && !closing}
          inert={closing}
          onAnimationEnd={event => {
            // the well's own slide only (not a child's animation bubbling up);
            // while closing that is the close: an opening cut short is cancelled
            if (closing && event.target === event.currentTarget) finishClosing()
          }}
          data-testid="account-menu-more"
          data-editing={editing ? 'true' : 'false'}>
          <div className="am-well-clip">
            <div className="am-more">
              {editing
                ? <React.Fragment>
                    <div className="am-edit-head">
                      <h6 id="account-menu-remove-title">Remove an account</h6>
                      <p>{REMOVE_HELP}</p>
                    </div>
                    <ul className="am-others" aria-labelledby="account-menu-remove-title">
                      {removable.map((account, index) =>
                        <RemoveRow
                          key={emailKey(account.email)}
                          account={account}
                          isCurrent={emailKey(account.email) === currentKey}
                          removeRef={element => {
                            removeRefs.current[emailKey(account.email)] = element
                          }}
                          onRemove={() => handleRemove(account, index)}
                        />
                      )}
                    </ul>
                    <div className="sa-remove-actions am-remove-actions">
                      <button type="button" ref={doneRef} className="btn sw-btn-primary sa-block" onClick={handleDone}>
                        Done
                      </button>
                      <button type="button" className="sa-forget-all" onClick={handleForgetAll}>
                        Forget all accounts on this device
                      </button>
                    </div>
                  </React.Fragment>
                : <React.Fragment>
                    {others.length > 0 &&
                      <ul className="am-others" aria-label="Other accounts on this device">
                        {others.map(account =>
                          <SignInRow key={emailKey(account.email)} account={account} target={target} onSignIn={handleSignIn} />
                        )}
                      </ul>}

                    <OffcanvasClose target={target}>
                      <button type="button" className="am-block am-row" aria-label="Add another account in a new tab" onClick={() => openSignInTab(ADD_ACCOUNT_PATH)} data-testid="account-menu-add">
                        <span className="am-row-icon" aria-hidden="true"><FontAwesomeIcon icon={faUserPlus} /></span>
                        <span>Add another account</span>
                      </button>
                    </OffcanvasClose>

                    {canEdit &&
                      <button type="button" ref={editRowRef} className="am-block am-row" onClick={handleEdit} data-testid="account-menu-edit">
                        <span className="am-row-icon" aria-hidden="true"><FontAwesomeIcon icon={faUserMinus} /></span>
                        <span>Remove an account</span>
                      </button>}

                    <OffcanvasClose target={target}>
                      <Link className="am-block am-row am-manage" to={MANAGE_ACCOUNT_PATH} data-testid="account-menu-manage">
                        <span className="am-row-icon" aria-hidden="true"><FontAwesomeIcon icon={faUserGear} /></span>
                        <span>Manage your EducationXR account</span>
                      </Link>
                    </OffcanvasClose>
                  </React.Fragment>}

              {/* Read when it changes, or when it takes focus; empty, it takes no room. */}
              <p ref={statusRef} className="am-status" tabIndex={-1} role="status" data-testid="account-menu-removed">
                {said.text}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sign out: always here, under the card (and the tray when open). */}
      <OffcanvasClose target={target}>
        <button type="button" className="am-sign-out" onClick={() => logout()} data-testid="account-menu-sign-out">
          <span className="am-row-icon" aria-hidden="true"><FontAwesomeIcon icon={faArrowRightFromBracket} /></span>
          <span>Sign out</span>
        </button>
      </OffcanvasClose>
    </section>
  )
}
